<template>
  <div
    class="NumberTickerInput"
    :class="{
      'NumberTickerInput--hasLabel': !excludeLabel,
    }"
    :data-size="size"
  >
    <div
      class="NumberTickerInput__container"
    >
      <div class="NumberTickerInput__labelContainer">
        <UIText
          v-if="!excludeLabel"
          weight="Medium"
          class="NumberTickerInput__label"
        >
          <Icon
            v-if="icon"
            :name="icon"
          /> {{ label }}
        </UIText>

        <UIText
          tag="div"
          class="NumberTickerInput__value"
          size="Inherit"
          weight="Medium"
          aria-live="polite"
        >
          {{ formattedValue }} <span class="NumberTickerInput__valueSuffix">{{ valueSuffix }}</span>
        </UIText>
      </div>

      <ol
        ref="scrollContainerRef"
        class="NumberTickerInput__scrollContainer"
        tabindex="0"
        role="region"
        :aria-label="label"
        @click="handleClick"
        @mousedown.prevent="handleMouseDown"
        @touchstart.prevent="handleTouchStart"
      >
        <UIText
          v-for="value in values"
          :key="value"
          tag="li"
          role="presentation"
          class="NumberTickerInput__tickerItem"
          :class="{
            'NumberTickerInput__tickerItem--isLargeIncrement': isLargeIncrement(value)
          }"
          :data-value="value"
        />
      </ol>

      <div
        ref="markerRef"
        class="NumberTickerInput__marker"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import UIText from "@cosine/components/UIText.vue";
import { onMounted, onBeforeUnmount, ref, useTemplateRef, onBeforeMount, computed } from "vue";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { Sound } from "@cosine/lib/utils/audio/Sound";
import { IconName } from "@cosine/components/Icon.types";
import Icon from "@cosine/components/Icon.vue";
import formatNumberToCompact from "@cosine/lib/utils/number/formatNumberToCompact";
import formatAmount from "@cosine/lib/utils/financial/formatAmount";
import { Currency, Money } from "@cosine/lib/api-models";

const modelValue = defineModel<Money>();

const {
  values,
  size = "large",
  label,
  icon,
  valueSuffix,
  numberFormat = "compact",
  excludeLabel = false,
} = defineProps<{
  values: Array<number>,
  size?: "small" | "large",
  label: string,
  excludeLabel?: boolean,
  icon?: IconName,
  valueSuffix?: string,
  numberFormat?: "compact" | "full",
}>();

const scrollContainerRef = useTemplateRef("scrollContainerRef");
const markerRef = useTemplateRef("markerRef");

const elWidth = ref(0);
const intersectionObserver = ref<IntersectionObserver | null>(null);
const resizeObserver = ref<ResizeObserver | null>(null);

const selectedValue = ref(modelValue.value?.Amount || 0);
const formattedValue = computed(() => {
  const amount = numberFormat === "compact" && selectedValue.value >= 10_000_00
    ? "$" + formatNumberToCompact(selectedValue.value / 100)
    : formatAmount(selectedValue.value, {
      excludeCents: true,
    });

  return selectedValue.value === maxValue ? `${amount}+` : amount;
});

const maxValue = values[values.length - 1];
const dragStartX = ref(0);
const scrollStartLeft = ref(0);
const hasDragged = ref(false);

const soundEffect = new Sound("/sounds/tick.mp3");

onBeforeMount(async () => {
  try {
    await soundEffect.load();
  } catch {
    // silently fail when loading sound effects
  }
});

onMounted(async () => {
  if (!scrollContainerRef.value || !markerRef.value) return;

  scrollToElementWithValue(selectedValue.value);

  elWidth.value = scrollContainerRef.value.clientWidth;

  resizeObserver.value = new ResizeObserver(handleResize);
  resizeObserver.value.observe(scrollContainerRef.value);

  await setupIntersectionObserver();
});

onBeforeUnmount(() => {
  intersectionObserver.value?.disconnect();
  resizeObserver.value?.disconnect();

  soundEffect.stop();
});

async function updateSelectedValue (value: number) {
  if (selectedValue.value === value) return;

  selectedValue.value = value;
  modelValue.value = {
    Amount: value,
    Currency: Currency.USD,
  };

  soundEffect.play({
    volume: isLargeIncrement(value) ? 1 : 0.25,
  });

  try {
    await Haptics.impact({
      style: ImpactStyle.Light,
    });
  } catch {
    // silently fail when haptic feedback is not available
  }
}

async function setupIntersectionObserver () {
  if (!scrollContainerRef.value || !markerRef.value) return;

  if (intersectionObserver.value) {
    intersectionObserver.value.disconnect();
  }

  const markerRect = markerRef.value.getBoundingClientRect();
  const scrollContainerRect = scrollContainerRef.value.getBoundingClientRect();

  const markerLeft = markerRect.left - scrollContainerRect.left;
  const markerWidth = markerRect.width;

  const leftMargin = markerLeft;
  const rightMargin = scrollContainerRect.width - markerLeft - markerWidth;

  const options = {
    root: scrollContainerRef.value,
    rootMargin: `0px -${rightMargin}px 0px -${leftMargin}px`,
    threshold: 0.1,
  };

  intersectionObserver.value = new IntersectionObserver(handleIntersection, options);

  const items = scrollContainerRef.value.querySelectorAll(".NumberTickerInput__tickerItem");
  items.forEach((item) => {
    intersectionObserver.value?.observe(item);
  });
}

function updateScrollPosition (clientX: number) {
  if (!scrollContainerRef.value) return;
  hasDragged.value = true;

  const deltaX = clientX - dragStartX.value;
  const {
    width,
  } = scrollContainerRef.value.getBoundingClientRect();

  const intervalWidth = width / values.length;
  const intervalsMoved = Math.round(deltaX / intervalWidth);
  const newScrollLeft = scrollStartLeft.value - intervalsMoved * intervalWidth;

  scrollContainerRef.value.scrollLeft = newScrollLeft;
}

async function handleIntersection (entries: Array<IntersectionObserverEntry>) {
  let highestRatio = 0;
  let nearestElement: HTMLElement | null = null;

  for (const entry of entries) {
    if (entry.intersectionRatio > highestRatio && entry.isIntersecting) {
      highestRatio = entry.intersectionRatio;
      nearestElement = entry.target as HTMLElement;
    }
  }

  if (nearestElement && nearestElement.dataset.value) {
    const value = parseInt(nearestElement.dataset.value);
    updateSelectedValue(value);
  }
}

function isLargeIncrement (value: number) {
  if (value === 0) return true;

  const index = values.indexOf(value);
  if (index <= 0) return false;

  const previousValue = values[index - 1];
  const difference = value - previousValue;
  return value % (10 * difference) === 0;
}

function handleClick (event: MouseEvent) {
  if (!event.target || !scrollContainerRef.value) return;

  if (hasDragged.value) {
    hasDragged.value = false;
    return;
  };

  const containerRect = scrollContainerRef.value.getBoundingClientRect();
  const midpoint = containerRect.left + (containerRect.width / 2);

  const selectedValueIndex = values.indexOf(selectedValue.value);
  const newValueIndex = event.clientX < midpoint
    ? Math.max(0, selectedValueIndex - 1)
    : Math.min(values.length - 1, selectedValueIndex + 1);
  const newValue = values[newValueIndex];

  updateSelectedValue(newValue);
  scrollToElementWithValue(newValue);
}

async function handleResize (entries: Array<ResizeObserverEntry>) {
  for (const entry of entries) {
    if (entry.target === scrollContainerRef.value) {
      if (entry.contentRect.width === elWidth.value) return;

      elWidth.value = entry.contentRect.width;
      await setupIntersectionObserver();
    }
  }
}

function handleMouseDown (event: MouseEvent) {
  if (!scrollContainerRef.value) return;

  dragStartX.value = event.clientX;
  scrollStartLeft.value = scrollContainerRef.value.scrollLeft;

  window.addEventListener("mousemove", handleMouseMove);
  window.addEventListener("mouseup", handleMouseUp);
}

function handleMouseUp () {
  window.removeEventListener("mousemove", handleMouseMove);
  window.removeEventListener("mouseup", handleMouseUp);
}

function handleMouseMove (event: MouseEvent) {
  updateScrollPosition(event.clientX);
}

function handleTouchStart (event: TouchEvent) {
  if (!scrollContainerRef.value) return;

  dragStartX.value = event.touches[0].clientX;
  scrollStartLeft.value = scrollContainerRef.value.scrollLeft;

  window.addEventListener("touchmove", handleTouchMove);
  window.addEventListener("touchend", handleTouchEnd);
}

function handleTouchEnd () {
  window.removeEventListener("touchmove", handleTouchMove);
  window.removeEventListener("touchend", handleTouchEnd);
}

function handleTouchMove (event: TouchEvent) {
  updateScrollPosition(event.touches[0].clientX);
}

function scrollToElementWithValue (value: number) {
  if (!scrollContainerRef.value) return;

  const element = scrollContainerRef.value.querySelector(`[data-value="${value}"]`) as HTMLElement;
  if (!element) return;
  scrollContainerRef.value.scrollLeft = element.offsetLeft - (scrollContainerRef.value.clientWidth / 2) + (element.clientWidth / 2);
}
</script>

<style lang="scss" scoped>
.NumberTickerInput {
  isolation: isolate;
  width: 100%;
  padding-top: 24px;
}

.NumberTickerInput__container {
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.NumberTickerInput__labelContainer {
  position: absolute;
  top: -24px;

  display: flex;
  flex-direction: column;
  align-items: center;
}

.NumberTickerInput__label {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 2px;
}

.NumberTickerInput__value {
  margin-bottom: 40px;

  font-size: 70px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  .NumberTickerInput[data-size="small"] & {
    margin-bottom: 32px;
    font-size: 56px;
  }
}

.NumberTickerInput__valueSuffix {
  position: relative;
  left: -16px;
  font-size: 16px;
}

.NumberTickerInput__scrollContainer {
  --tickHeight: 80px;
  --valueHeight: 140px;
  --maskWidth: 80px;
  position: relative;

  display: flex;
  width: 100%;
  height: calc(var(--tickHeight) + var(--valueHeight));
  overflow-x: auto;

  mask-image: linear-gradient(
    to right,
    transparent 0,
    black var(--maskWidth),
    black calc(100% - var(--maskWidth)),
    transparent 100%,
  ), linear-gradient(
    to left,
    transparent 0,
    black var(--maskWidth),
    black calc(100% - var(--maskWidth)),
    transparent 100%,
  );

  cursor: ew-resize;

  scroll-snap-type: x mandatory;

  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &:focus-visible {
    outline: none;
  }

  .NumberTickerInput:not(.NumberTickerInput--hasLabel) & {
    --valueHeight: 120px;
  }

  .NumberTickerInput[data-size="small"] & {
    --tickHeight: 32px;
    --valueHeight: 110px;
  }

  .NumberTickerInput[data-size="small"]:not(.NumberTickerInput--hasLabel) & {
    --valueHeight: 100px;
  }

  &::before,
  &::after {
    display: block;
    flex-shrink: 0;
    width: 50%;

    content: "";
  }
}

.NumberTickerInput__tickerItem {
  position: relative;

  flex-shrink: 0;
  align-self: flex-end;
  width: calc(1px + 8px);
  height: var(--tickHeight);

  scroll-snap-align: center;

  &::before {
    position: absolute;
    left: 4px;

    width: 1px;
    height: 100%;

    background-color: var(--colorSwissBlack);

    content: "";
  }
}

.NumberTickerInput__tickerItem--isLargeIncrement {
  height: 96px;

  .NumberTickerInput[data-size="small"] & {
    height: 40px;
  }
}

.NumberTickerInput__marker {
  position: absolute;
  left: calc(50% - 1px);
  bottom: 0;

  width: 2px;
  height: 120px;

  background-color: var(--colorSwissBlack);

  pointer-events: none;

  .NumberTickerInput[data-size="small"] & {
    height: 56px;
  }
}
</style>
